import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { cn } from '@/shared/ui/cn';
import { IconButton } from '@/shared/ui/IconButton';

/**
 * Largeur d'une page, selon ce qu'elle porte :
 *   narrow   un formulaire, un texte, un reçu (560)
 *   default  une liste, une fiche (720)
 *   wide     un tableau de bord, une grille, deux colonnes (1080)
 *   full     le fil plein écran, un direct, une messagerie
 */
export type PageWidth = 'narrow' | 'default' | 'wide' | 'full';

const WIDTH: Record<PageWidth, string> = {
  narrow: 'max-w-[560px]',
  default: 'max-w-[720px]',
  wide: 'max-w-[1080px]',
  full: 'max-w-none',
};

/** Pose le titre de l'onglet du navigateur : « Commandes · BUL ». */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    document.title = title ? `${title} · BUL` : 'BUL';
  }, [title]);
}

/**
 * Retour arrière : la page précédente si l'on vient du site, sinon la page de
 * repli (un lien ouvert depuis une notification ou un message n'a pas d'avant).
 */
export function useBack(fallback = '/'): () => void {
  const navigate = useNavigate();
  return () => {
    const index = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (index > 0) {
      void navigate(-1);
    } else {
      void navigate(fallback, { replace: true });
    }
  };
}

export type PageHeaderProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Bouton retour : `true` (page précédente) ou une adresse de repli. */
  back?: boolean | string;
  /** Actions de la page (icônes, bouton secondaire) : à droite du titre. */
  actions?: ReactNode;
  /** Titre de destination d'onglet (Accueil, Messages) : plus grand, sans retour. */
  large?: boolean;
  /** Filet sous l'en-tête, sur téléphone. */
  bordered?: boolean;
  className?: string;
};

/**
 * En-tête de page. Sur téléphone, une barre collée en haut (retour, titre sur
 * une ligne, actions) comme l'application ; sur ordinateur, un titre de page
 * dans la colonne, avec ses actions à droite.
 */
export function PageHeader({
  title,
  subtitle,
  back,
  actions,
  large = false,
  bordered = true,
  className,
}: PageHeaderProps) {
  const { t } = useTranslation();
  const goBack = useBack(typeof back === 'string' ? back : '/');

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex min-h-12 items-center gap-2.5 bg-surface/95 px-3 py-1.5 safe-top backdrop-blur-md',
        'md:static md:min-h-0 md:bg-transparent md:px-0 md:pt-8 md:pb-5 md:backdrop-blur-none',
        bordered && 'border-b border-line md:border-b-0',
        className,
      )}
    >
      {back ? (
        <IconButton
          name="arrowLeft"
          label={t('action.back')}
          onClick={goBack}
          className="-ml-1 md:-ml-2"
        />
      ) : null}
      <div className="min-w-0 flex-1 pl-1 md:pl-0">
        {title ? (
          <h1
            className={cn(
              'truncate text-fg',
              large ? 'text-title1 md:text-display' : 'text-title2 md:text-display',
            )}
          >
            {title}
          </h1>
        ) : null}
        {subtitle ? (
          <p className="mt-px truncate text-caption text-fg-muted md:mt-1 md:text-subtle">
            {subtitle}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-1 md:gap-2">{actions}</div> : null}
    </header>
  );
}

export type PageProps = PageHeaderProps & {
  width?: PageWidth;
  /** Titre de l'onglet du navigateur, si le titre affiché n'est pas une chaîne. */
  documentTitle?: string;
  /** Retire l'en-tête (la page dessine le sien). */
  bare?: boolean;
  /** Barre d'actions : collée en bas sur téléphone, en fin de colonne sur ordinateur. */
  actionBar?: ReactNode;
  /**
   * La page occupe toute la hauteur de l'écran sur téléphone, pour qu'une barre
   * d'envoi (`FormActions` dans un formulaire posé directement dans la page)
   * reste en bas même quand le contenu est court. Avec `actionBar`, la page
   * prend déjà toute la hauteur ; `fill` étire en plus le contenu en colonne.
   */
  fill?: boolean;
  contentClassName?: string;
  children: ReactNode;
};

/**
 * Le gabarit de toutes les pages : un en-tête, une colonne à la bonne largeur,
 * et au plus une barre d'actions. Les marges latérales (16 sur téléphone, 24 à
 * partir de la tablette) sont portées ici, une fois pour toutes.
 */
export function Page({
  width = 'default',
  documentTitle,
  bare = false,
  actionBar,
  fill = false,
  contentClassName,
  children,
  ...header
}: PageProps) {
  useDocumentTitle(documentTitle ?? (typeof header.title === 'string' ? header.title : undefined));
  const fills = fill || Boolean(actionBar);
  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col md:px-6',
        WIDTH[width],
        // Toute la hauteur utile (l'ossature pose `--page-fill` sous la barre d'onglets).
        fills && 'min-h-[var(--page-fill,100dvh)] md:min-h-0',
      )}
    >
      {bare ? null : <PageHeader {...header} />}
      <div
        className={cn(
          'flex-1 px-4 pb-6 md:px-0 md:pb-10',
          // Un formulaire posé directement dans la page s'étire à son tour : sa
          // barre d'envoi (`FormActions`) descend en bas de l'écran.
          fill && 'flex flex-col [&>form]:flex [&>form]:flex-1 [&>form]:flex-col',
          contentClassName,
        )}
      >
        {children}
      </div>
      {actionBar ? <ActionBar>{actionBar}</ActionBar> : null}
    </div>
  );
}

/**
 * Barre d'action : elle porte l'étape suivante, et contient au plus un bouton
 * `primary`. Collée en bas de l'écran sur téléphone, au-dessus de la barre
 * d'onglets quand la page en a une (l'ossature pose `--bottom-bar`), tout en bas
 * sinon, avec la marge de la zone sûre. Sur ordinateur, elle se pose en fin de
 * colonne, où l'œil la cherche.
 */
export function ActionBar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky bottom-[var(--bottom-bar,0px)] z-10 flex flex-wrap items-center gap-2.5 border-t border-line bg-surface px-4 pt-3',
        'pb-[calc(12px+var(--action-bar-safe,env(safe-area-inset-bottom,0px)))]',
        'md:bottom-0 md:mx-0 md:rounded-none md:border-t md:bg-surface/95 md:px-0 md:py-4 md:backdrop-blur-md',
        className,
      )}
    >
      {children}
    </div>
  );
}
